/**
 * @fileoverview The share studio's shared shapes: the dialog's props, what a
 * season's share hands it, the stand-in ids (a season's card, the collage's
 * slide), the switches' orders, a photograph's place in its frame, and the
 * story and the finished set as the studio keeps them. Moved out of
 * plant-list/components/dialogs/SharePlantDialog (batch 18, A105); nothing in
 * it changed.
 *
 * Where: the dialog (SharePlantDialog, which passes SeasonShare and
 * SeasonCardChoices on to the season review) and its hooks and bands
 * (features/share/studio).
 *
 * @module shareStudioTypes
 */

import type { CropFocus } from '@/shared/lib/photoFit';
import type { PlantPhoto } from '@/shared/types/photo';
import type { Subject } from '@/shared/api/subject';
import type { LinkKey } from '@/shared/lib/socialLinks';
import type { ShareLayout, ShareSize } from '@/features/share/frames/frameOptions';

/** The plant or location to share, and its photographs. */
export interface SharePlantDialogProps {
  /** Whether the sheet is open. Opening it starts a new story. */
  isOpen: boolean;
  /**
   * Closes the sheet. Called through closeSheet (Cancel, Escape, a tap outside
   * it, Add a photo), which forgets the share in progress first; never
   * directly, or the draft would outlive the sheet and put it back uninvited.
   */
  onClose: () => void;
  /** The plant being shared. With a location's subject it only names the saved files. */
  plantObjId: number;
  /** A location's share: its own photos and link, its name on the frame. */
  subject?: Subject;
  /** The crop, e.g. Vining Cherry Tomato. */
  name: string;
  /** The cultivar, e.g. Black Cherry. */
  variety: string;
  /** Every photograph the plant has, in any order. */
  photos: PlantPhoto[];
  /** The photograph the card was showing when Share was tapped. It goes first. */
  startPhotoId?: number | null;
  /** Opens the photo screen, for a plant with nothing to share yet. */
  onAddPhoto?: () => void;
  /**
   * A location's share: the plant a photo is of, for one of its plants'
   * photos (their ids made negative), with the plant's own id. Null for the
   * location's cover and own photos.
   */
  plantOf?: (photoId: number) => { name: string; variety: string; plantObjId: number } | null;
  /** A location's share: how many plants grow there, for its cover's line. */
  plantCount?: number;
  /** The photographs picked when the sheet opens, in order: the season's card and a few of its photos. */
  startPhotoIds?: number[];
  /** A season's share: its card goes first, and the link is the season's. */
  season?: SeasonShare;
  /** The sheet's heading, in place of "Share" and the variety. */
  title?: string;
  /** The caption starter (captionStarter): the subject's facts, emojis and hashtags, filled into the caption to edit. */
  captionStarter?: string;
}

/** What the sheet hands the page that makes a season's card. */
export interface SeasonCardChoices {
  /** The frame's size. */
  size: ShareSize;
  /** The card's line: the one for all, or the card's own. */
  words: string;
  /** The handle, when printed. */
  handle: { text: string; place: LinkKey } | null;
  /** The plant photos going out, in order; the page puts the first few on the card. */
  photos: Array<{ plantObjId: number; photoId: number }>;
}

/** A season's share: what the sheet needs from the season review. */
export interface SeasonShare {
  /** The season's year. */
  year: number;
  /** The season is still under way ("Your season so far"). */
  soFar: boolean;
  /** The season's Public switch, sent with its first link. */
  isPublic: boolean;
  /** Everything the card is made from that the sheet does not choose, as one key. */
  cardKey: string;
  /** Makes the season's card for the sheet's choices. */
  buildCard: (choices: SeasonCardChoices) => Promise<File>;
  /** The words that go with the share when the gardener writes none. */
  caption: string;
  /** Told once the link is saved and its pictures sent. */
  onShared?: () => void;
}

/** The season card's place among a season's photographs: their ids are negative, so 0 is free. */
export const SEASON_CARD_ID = 0;
/**
 * The collage's own slide in the preview (layout collage): stands for the
 * collage picture among the photographs, never among the strip's. Far below
 * any photo id, a location's plant photos' negative ones included.
 */
export const COLLAGE_ID = Number.MIN_SAFE_INTEGER;

/** The cover's switches, in order. */
// No date: the cover has none, and a location's own pictures are not dated on the frame.
export const COVER_KEYS = ['name', 'count', 'handle'] as const;
/** The plant photos' switches, in order: a plant share's, and the location. */
// No stage: photos carry none (2026-10-03).
export const PLANT_KEYS = ['name', 'variety', 'date', 'place', 'handle'] as const;

/** The switches, in the order they are printed on the frame. */
export const ELEMENT_KEYS = ['name', 'variety', 'date', 'words', 'handle'] as const;

/** The photograph open in Move & zoom, and the shape of the frame (or collage tile) it fills. */
export type Editing = { photo: PlantPhoto; aspect: number };

/**
 * Where a photograph sits in its frame (0..1 across and down), how far it is zoomed in
 * (1 = just filling it) and, once the gardener has cropped it, the shape of the slot
 * it was cropped in (a: width over height), so it is refitted when the size or the
 * layout changes (refitFocus, B86).
 */
export type Focus = CropFocus;
/** A photograph centred in its frame, until the gardener drags it. */
export const CENTRE: Focus = { x: 0.5, y: 0.5 };

/*
 * The preview's tap slop, swipe distance and speed, the pause before the
 * pictures are made again, and the download pacing are tunables
 * (SHARE_TAP_SLOP_PX, SHARE_SWIPE_PX, SHARE_SWIPE_MS, SHARE_REBUILD_DELAY_MS,
 * SHARE_DOWNLOAD_GAP_MS, SHARE_DOWNLOAD_REVOKE_MS).
 */

/** The story going out, and the picture in view: kept as one, so a tap reads both as they stand. */
export type Selection = { picked: number[]; shown: number | null };
/**
 * A finished set of pictures: what it was made from, the photographs it holds
 * in order, whether a season's card heads it, and the files.
 */
export type BuiltFrames = { key: string; order: number[]; layout: ShareLayout; cardIn: boolean; collageIn: boolean; files: File[] };
